<template>
  <div
    class="play-picker"
    :class="{ 'is-error': !!error, 'is-disabled': disabled }"
  >
    <label
      v-if="label"
      class="play-picker__label"
    >
      {{ label }}
      <span
        v-if="required"
        class="play-picker__req"
        aria-hidden="true"
      >*</span>
    </label>

    <flat-pickr
      class="play-picker__field"
      :value="value"
      :config="mergedConfig"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="$emit('input', $event)"
      @on-change="(selected, str) => $emit('change', str)"
    />

    <p
      v-if="error || hint"
      class="play-picker__msg"
      :class="error ? 'play-picker__msg--error' : 'play-picker__msg--hint'"
    >
      {{ error || hint }}
    </p>
  </div>
</template>

<script>
/**
 * PlayDatePicker — Play-styled date field over flatpickr. Replaces
 * bootstrap-vue's <b-form-datepicker>. v-model holds the string value; pass
 * advanced flatpickr options (locale, disable, minDate/maxDate, firstDayOfWeek)
 * through `config`. Emits `input` (v-model) and `change` (committed string).
 */
import flatPickr from 'vue-flatpickr-component'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'flatpickr/dist/flatpickr.css'

export default {
  name: 'PlayDatePicker',
  components: { flatPickr },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [String, Date, Array, Number],
      default: '',
    },
    /** flatpickr config; merged over the defaults below */
    config: {
      type: Object,
      default: () => ({}),
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    mergedConfig() {
      return {
        altInput: true,
        altFormat: 'D, d M Y',
        dateFormat: 'Y-m-d',
        ...this.config,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-picker {
  display: block;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 14px;
    color: var(--text-strong);
  }
  &__req { color: var(--c-danger); }

  /* flatpickr renders an <input> (plus an altInput when enabled) — style both
     as the Play soft-fill control */
  ::v-deep input {
    display: block;
    width: 100%;
    padding: 12px 15px;
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 15px;
    color: var(--text-strong);
    background: var(--surface-sunk);
    border: var(--border-w) solid transparent;
    border-radius: var(--radius);
    box-shadow: none;
    outline: none;
    transition:
      background var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);

    &::placeholder { color: var(--text-muted); }
    &:focus,
    &.active {
      background: var(--surface-card);
      border-color: var(--c-secondary);
      box-shadow: 0 0 0 3px var(--c-secondary-soft);
    }
  }
  ::v-deep input.flatpickr-input[readonly] { cursor: pointer; }

  &__msg {
    margin: 6px 0 0;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: 500;

    &--error { color: var(--c-danger-strong); }
    &--hint { color: var(--text-muted); }
  }

  &.is-error ::v-deep input {
    background: var(--c-danger-soft);
    &:focus,
    &.active { border-color: var(--c-danger); box-shadow: 0 0 0 3px var(--c-danger-soft); }
  }
  &.is-disabled { opacity: .55; pointer-events: none; }
}
</style>
